/**
 * Brand-specific values, in one place.
 *
 * WHY THIS FILE EXISTS
 * --------------------
 * One codebase serves three brands, each on its own branch (lr_dev, pg_dev,
 * mb_dev). Before this file the brand leaked into six unrelated source files,
 * so every shared change had to be made twice by hand and the branches drifted.
 * Anything that differs per brand belongs HERE. Every other file in src/ must
 * be identical on every branch.
 *
 * If you are about to hardcode a brand name, a logo, or a per-brand host
 * anywhere else: add it to this file instead.
 *
 * HOW THE BRAND IS CHOSEN
 * -----------------------
 * From VUE_APP_BRAND when it is set. Otherwise it is derived from
 * VUE_APP_API_URL, which already differs per brand and which nobody can forget
 * to set, because the panel cannot talk to anything without it. That means no
 * .env change is required to adopt this file.
 */

// The La Ruota bitmap; also what a brand this file does not know falls back to.
import bundledMark from "@/assets/images/lr-logo.png";
// Pizza Garden's own marks, the same files as on its website, so that a failed load of the
// website copy still shows Pizza Garden and never another brand's mark.
import pizzaGardenLight from "@/assets/images/pg-logo-light.png";
import pizzaGardenDark from "@/assets/images/pg-logo-dark.png";
// La Ruota's wordmark, cut from its website's logo (www.laruota.ca/images/logo_b.png and logo.png)
// without the "Authentic Neapolitan Pizza" line, which no one could read at sidebar size.
import laRuotaLight from "@/assets/images/lr-logo-light.png";
import laRuotaDark from "@/assets/images/lr-logo-dark.png";

const BRANDS = {
  laruota: {
    key: "laruota",
    name: "La Ruota",
    // Deliverect pushes the menu to Uber / DoorDash / Skip. One host per brand.
    deliverectSyncBase: "https://deliverectlr.androvega.ca/sync",
    // The link scheme the brand's apps open when a push or a banner is tapped (TapTargetPicker,
    // utils/tapLink.js; the API's Brand class holds the same). null: the apps open none.
    appScheme: "laruota://",
    mark: {
      type: "image",
      light: laRuotaLight,
      dark: laRuotaDark,
      fallback: { light: laRuotaLight, dark: laRuotaDark },
      alt: "La Ruota",
    },
  },
  pizzagarden: {
    key: "pizzagarden",
    name: "Pizza Garden",
    deliverectSyncBase: "https://deliverectpg.androvega.ca/sync",
    appScheme: "pizzagarden://",
    // The website's domain: its pictures are fetched without cookies (src/utils/imageLoading.js).
    // A brand without one keeps loading pictures the usual way.
    website: "pizzagarden.ca",
    mark: {
      // Served from the brand website, so it can be changed without a panel build.
      // The bundled copies cover the site being unreachable or refusing the browser.
      type: "image",
      light: "https://pizzagarden.ca/images/logo_b.png",
      dark: "https://pizzagarden.ca/images/logo.png",
      fallback: { light: pizzaGardenLight, dark: pizzaGardenDark },
      alt: "Pizza Garden",
    },
  },
};

// A brand we do not have values for still has to render something. It gets the
// bundled mark and NO Deliverect host, so a menu sync cannot be pushed to the
// wrong brand: the button that uses it checks for null and stays disabled.
const UNKNOWN = {
  key: "unknown",
  name: "Androvega",
  deliverectSyncBase: null,
  appScheme: null,
  mark: {
    type: "image",
    light: bundledMark,
    dark: bundledMark,
    fallback: bundledMark,
    alt: "Androvega",
  },
};

function resolveKey() {
  const explicit = String(import.meta.env.VUE_APP_BRAND || "").trim().toLowerCase();
  if (explicit) return explicit;

  const api = String(import.meta.env.VUE_APP_API_URL || "").toLowerCase();
  if (api.includes("laruota")) return "laruota";
  if (api.includes("pizzagarden")) return "pizzagarden";
  if (api.includes("megabite")) return "megabite";
  return "";
}

const key = resolveKey();
// A sandbox build shares store hashes with production, so it must never reach the real
// Deliverect host: VUE_APP_DELIVERECT_SYNC=off leaves the Sync Deliverect button disabled.
const deliverectOff = String(import.meta.env.VUE_APP_DELIVERECT_SYNC || "").trim().toLowerCase() === "off";
const chosen = BRANDS[key] || UNKNOWN;
const brand = deliverectOff ? { ...chosen, deliverectSyncBase: null } : chosen;

if (!BRANDS[key]) {
  // Loud, because the alternative is a panel that quietly wears the wrong name.
  // eslint-disable-next-line no-console
  console.error(
    "[brand] Could not resolve a brand from VUE_APP_BRAND or VUE_APP_API_URL " +
      "(got " +
      JSON.stringify(key) +
      "). Falling back to a neutral mark and disabling the Deliverect sync. " +
      "Add the brand to src/brand.js."
  );
}

export default brand;
